<template>
  <div class="page">
    <div
      v-if="(sidebarOpen || sidebarSwipeActive) && isMobile"
      class="drawer-backdrop"
      :style="drawerBackdropStyle"
      @click="closeSidebar"
    ></div>

    <div
      v-if="isMobile && !sidebarOpen"
      class="edge-swipe-zone"
      @touchstart.passive="handleSidebarEdgeStart"
      @touchmove="handleSidebarEdgeMove"
      @touchend="handleSidebarEdgeEnd"
      @touchcancel="handleSidebarEdgeCancel"
    ></div>

    <aside ref="sidebarShell" class="sidebar-shell" :class="{ open: sidebarOpen || sidebarSwipeActive, mobile: isMobile }" :style="sidebarShellStyle">
      <NoteSidebar
        :lists="visibleLists"
        :groups="groups"
        :selected-list-id="selectedListId"
        :mode="mode"
        :billing="billing"
        :current-user-id="Number(user?.id || 0)"
        @select-list="handleSelectList"
        @create-list="requestCreateList"
        @open-groups="openGroups"
        @open-auth="openAuth"
        @open-account="goToAccount"
        @upgrade="plansModalOpen = true"
        @manage-billing="handleManageBilling"
        @request-move-list="requestMoveList"
        @close="sidebarOpen = false"
      />
    </aside>

    <div class="workspace" :class="{ shifted: sidebarOpen && !isMobile }">
      <header class="topbar">
        <div class="topbar-left">
          <button class="nav-toggle" @click="sidebarOpen = !sidebarOpen" :aria-label="sidebarOpen ? 'Cerrar panel' : 'Abrir panel'">
            <svg class="nav-toggle-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>

          <div class="brand-block ">
            <div>
              <div class="brand-line">
                <h1>{{ currentListTitle }}</h1>
                <SyncStatusPill
                  v-if="mode === 'user'"
                  :online="online"
                  :syncing="syncing"
                  :socket-connected="socketConnected"
                  compact
                />
              </div>
              <p>{{ headerDescription }}</p>
            </div>
          </div>
        </div>

        <div class="top-actions">
          <button v-if="mode === 'user'" class="top-btn muted new-sheet-btn" @click="requestCreateList({})">Nueva hoja</button>

          <div v-if="mode === 'user' && notificationsEnabled" class="notifications-shell">
            <button
              class="bell-btn"
              :class="{ active: notificationsMenuOpen, hasUnread: notificationsUnread > 0 }"
              type="button"
              aria-label="Notificaciones"
              title="Notificaciones"
              @click.stop="toggleNotificationsMenu"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" class="bell-icon">
                <path d="M12 3a5 5 0 0 0-5 5v2.2c0 .82-.2 1.62-.58 2.35L5 15.5h14l-1.42-2.95a5.4 5.4 0 0 1-.58-2.35V8a5 5 0 0 0-5-5Zm0 18a2.7 2.7 0 0 1-2.53-1.76h5.06A2.7 2.7 0 0 1 12 21Z" />
              </svg>
              <span v-if="notificationsUnread > 0" class="bell-badge">{{ notificationsUnread > 99 ? '99+' : notificationsUnread }}</span>
            </button>

            <div v-if="notificationsMenuOpen" class="notifications-popover" @click.stop @pointerdown.stop>
              <div class="notifications-head">
                <div>
                  <strong>Notificaciones</strong>
                  <small>{{ notificationsUnread ? `${notificationsUnread} sin leer` : 'Todo al día' }}</small>
                </div>
                <button class="soft-inline-btn" type="button" @click="openAccountFromNotifications">Mi cuenta</button>
              </div>

              <div v-if="notificationsLoading" class="notifications-empty">Cargando actividad…</div>
              <div v-else-if="!notificationsItems.length" class="notifications-empty">Todavía no hay actividad nueva en tus hojas grupales.</div>
              <div v-else class="notifications-list">
                <button
                  v-for="item in notificationsItems"
                  :key="item.id"
                  type="button"
                  class="notification-item"
                  :class="{ unread: !item.read }"
                  @click="handleNotificationSelect(item)"
                >
                  <div class="notification-main">
                    <strong>{{ item.title }}</strong>
                    <p>{{ item.message }}</p>
                  </div>
                  <div class="notification-meta">
                    <span>{{ item.time_label }}</span>
                    <small>{{ item.group_name || item.list_title || 'Hoja grupal' }}</small>
                  </div>
                </button>
              </div>
            </div>
          </div>

          <button v-if="canShowInstallButton" class="top-btn muted" @click="promptInstall">Instalar app</button>
          <button v-if="mode === 'guest'" class="top-btn primary" @click="openAuth">Iniciar sesión</button>
          <template v-else-if="!isMobile">
            <button class="top-btn muted" @click="goToAccount">Mi cuenta</button>
            <button class="top-btn muted" @click="doLogout">Salir</button>
          </template>
        </div>
      </header>

      <main class="content-area">
        <AdBanner v-if="showAds" class="top-ad" :enabled="showAds" />

        <section
          ref="composerCard"
          class="composer-card"
          :class="{ dragging: isComposerDragOver }"
          @dragenter.prevent="handleComposerDragEnter"
          @dragover.prevent="handleComposerDragEnter"
          @dragleave.prevent="handleComposerDragLeave"
          @drop.prevent="handleComposerDrop"
        >
          <div v-if="isComposerDragOver" class="composer-drop-overlay">Suelta tu imagen aquí</div>

          <div class="composer-copy">
            <div class="composer-label">Nueva tarea</div>
            <p>Escribe aquí y añádela a tu hoja actual.</p>
          </div>

          <div class="composer-main">
            <input
              ref="taskComposerTop"
              v-model.trim="newTask"
              class="composer-input"
              type="text"
              inputmode="text"
              enterkeyhint="done"
              placeholder="Escribe una tarea y presiona Enter"
              @focus="composerVisible = true"
              @keydown.enter.prevent="handleAddTask"
            >

            <label class="composer-attach" :class="{ disabled: mode === 'guest' || !online }" title="Agregar imagen">
              <input
                type="file"
                class="composer-file"
                accept=".png,.jpg,.jpeg,.svg,.heic,.heif,image/png,image/jpeg,image/svg+xml,image/heic,image/heif"
                :disabled="mode === 'guest' || !online"
                @change="handleComposerImage"
              >
              <span aria-hidden="true">🖼</span>
            </label>

            <button class="composer-button" @click="handleAddTask">
              <span class="desktop-copy">Agregar</span>
              <span class="mobile-copy">＋</span>
            </button>
          </div>

          <div v-if="composerImagePreview" class="composer-preview">
            <img :src="composerImagePreview" alt="Vista previa" class="composer-preview-image">
            <div class="composer-preview-copy">
              <strong>{{ composerImageFile?.name }}</strong>
              <span>Se guardará junto con la tarea al crearla.</span>
            </div>
            <button class="ghost-btn danger" type="button" @click="clearComposerImage">Quitar</button>
          </div>
        </section>

        <section class="paper-shell" :style="paperStyle">
          <div class="paper-head">
            <div class="paper-head-copy">
              <div class="paper-kicker">{{ currentListMeta }}</div>

              <div class="paper-title-row">
                <template v-if="editingTitle && currentList && !isQuickSheet">
                  <input
                    ref="titleEditor"
                    v-model.trim="editingTitleValue"
                    class="paper-title-input"
                    @keydown.enter.prevent="commitTitleEdit"
                    @blur="commitTitleEdit"
                  >
                </template>
                <template v-else>
                  <h2 class="paper-title-text">{{ currentList?.title || 'Mis notas' }}</h2>
                  <button v-if="currentList && !isQuickSheet" class="title-edit-btn" title="Cambiar título" @click="startTitleEdit">✎</button>
                </template>
              </div>

              <div class="save-status">{{ saveStatus }}</div>

              <div class="paper-inline-actions">
                <button v-if="isQuickSheet && currentTasks.length" class="ghost-btn save-btn" @click="openQuickSaveModal">Guardar</button>
                <button
                  class="ghost-btn danger sheet-delete-btn"
                  v-if="currentList && !isQuickSheet && canDeleteCurrentList"
                  @click="handleDeleteList(currentList.id)"
                >
                  Eliminar
                </button>
              </div>
            </div>

            <div class="paper-head-actions">
              <div class="note-color-area">
                <button class="color-icon-btn" type="button" title="Color de la nota" aria-label="Color de la nota" @click.stop="toggleColorMenu">
                  <span class="color-preview large" :style="{ backgroundColor: resolvedListColor }"></span>
                </button>

                <div v-if="colorMenuOpen" class="palette-popover right" @click.stop @mousedown.stop @pointerdown.stop>
                  <div class="palette-label">Color de la nota</div>
                  <div class="palette-swatches">
                    <button
                      v-for="color in noteColors"
                      :key="color"
                      class="swatch"
                      :class="{ active: resolvedListColor === color }"
                      :style="{ backgroundColor: color }"
                      @click="handleChangeListColor(color)"
                    ></button>
                  </div>

                  <label class="custom-color" @click.stop @mousedown.stop @pointerdown.stop>
                    <span>Personalizado</span>
                    <input type="color" :value="resolvedListColor" @click.stop @mousedown.stop @pointerdown.stop @input="handleChangeListColor($event.target.value)">
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div class="paper">
            <div class="margin-line"></div>

            <ul class="task-list">
              <TaskItem
                v-for="task in orderedTasks"
                :key="task.id"
                :task="task"
                :accent-color="resolvedListColor"
                :allow-image="mode === 'user'"
                :upload-disabled="!online"
                :busy="pendingTaskIds.includes(task.id)"
                :is-dragging="draggingTaskId === task.id"
                :drop-target="!!(taskDropTarget && taskDropTarget.taskId === task.id)"
                :draggable="currentTasks.length > 1 && !pendingTaskIds.includes(task.id)"
                @toggle="handleToggleTask"
                @rename="handleRenameTask"
                @delete="handleDeleteTask"
                @upload-image="handleAttachImage"
                @remove-image="handleRemoveTaskImage"
                @drag-start="handleTaskDragStart"
                @drag-end="handleTaskDragEnd"
                @drag-enter="handleTaskDragEnter"
                @drop-task="handleTaskDrop"
                @touch-drag-start="handleTaskTouchStart"
                @touch-drag-move="handleTaskTouchMove"
                @touch-drag-drop="handleTaskTouchDrop"
                @touch-drag-end="handleTaskTouchEnd"
              />
            </ul>


            <div v-if="!currentTasks.length" class="empty-state">
              <div class="empty-icon">✎</div>
              <p>Esta hoja todavía está vacía.</p>
              <span>Usa el campo superior para agregar tu primera tarea.</span>
            </div>
          </div>

          <div v-if="showAuthorFooter" class="paper-footer">
            Autor de la hoja: <strong>{{ currentList.author_name || 'Usuario original' }}</strong>
          </div>
        </section>

        <AdBanner v-if="showAds" class="bottom-ad" :enabled="showAds" />
      </main>
    </div>

    <transition name="fab-fade">
      <button
        v-if="showFloatingComposerFab"
        class="floating-composer-fab"
        type="button"
        aria-label="Nueva tarea"
        @click="openFloatingComposer"
      >
        <span>＋</span>
      </button>
    </transition>

    <transition name="composer-fade">
      <div
        v-show="showFloatingComposerModal"
        class="floating-composer-overlay"
        :style="floatingComposerOverlayStyle"
        @click.self="closeFloatingComposer"
      >
        <section class="composer-card floating-composer-card" :style="floatingComposerCardStyle">
        <div class="composer-copy">
          <div class="composer-label">Nueva tarea</div>
          <p>Prepara la tarea aquí y agrégala cuando esté lista.</p>
        </div>

        <div class="composer-main floating-composer-main">
          <input
            ref="taskComposerFloating"
            v-model.trim="newTask"
            class="composer-input"
            type="text"
            inputmode="text"
            enterkeyhint="done"
            placeholder="Escribe una tarea y presiona Enter"
            @keydown.enter.prevent="handleAddTask"
          >

          <label class="composer-attach" :class="{ disabled: mode === 'guest' || !online }" title="Agregar imagen">
            <input
              type="file"
              class="composer-file"
              accept=".png,.jpg,.jpeg,.svg,.heic,.heif,image/png,image/jpeg,image/svg+xml,image/heic,image/heif"
              :disabled="mode === 'guest' || !online"
              @change="handleComposerImage"
            >
            <span aria-hidden="true">🖼</span>
          </label>

          <button class="composer-button" @click="handleAddTask">
            <span>Agregar</span>
          </button>
        </div>

        <div v-if="composerImagePreview" class="composer-preview">
          <img :src="composerImagePreview" alt="Vista previa" class="composer-preview-image">
          <div class="composer-preview-copy">
            <strong>{{ composerImageFile?.name }}</strong>
            <span>La imagen se conservará aunque cierres este cuadro.</span>
          </div>
          <button class="ghost-btn danger" type="button" @click="clearComposerImage">Quitar</button>
        </div>
      </section>
    </div>
    </transition>

    <AuthModal :open="authModalOpen" :login-action="loginAction" :register-action="registerAction" @close="closeAuth" />

    <GroupManagerModal
      :open="groupModalOpen"
      :groups="groups"
      :limit-message="groupLimitMessage"
      @close="closeGroups"
      @create-group="handleCreateGroup"
      @invite="openInvitePrompt"
      @leave-group="handleLeaveGroup"
      @delete-group="handleDeleteGroup"
      @delegate-group="handleDelegateGroup"
    />

    <CreateListModal
      :open="createListModalOpen"
      :groups="groups"
      :preset-group-id="createListPresetGroupId"
      :heading="listModalHeading"
      :eyebrow="listModalEyebrow"
      :description="listModalDescription"
      :submit-label="listModalSubmitLabel"
      :placeholder="listModalPlaceholder"
      @close="closeCreateListModal"
      @submit="submitCreateList"
    />

    <MoveListModal
      :open="moveListModalOpen"
      :title="moveListModalTitle"
      :description="moveListModalDescription"
      :state="moveListModalState"
      :countdown="moveListModalCountdown"
      @close="closeMoveListModal"
      @confirm="confirmMoveList"
    />

    <PlanModal :open="plansModalOpen" :plans="billing.plans || []" :heading="planModalHeading" :context-message="planModalMessage" @close="closePlansModal" @select="handleUpgrade" />

    <ConfirmActionModal
      :open="confirmActionModalOpen"
      :title="confirmActionModalTitle"
      :description="confirmActionModalDescription"
      :confirm-label="confirmActionModalConfirmLabel"
      :loading="confirmActionModalLoading"
      :tone="confirmActionModalTone"
      @close="closeConfirmActionModal"
      @confirm="confirmActionModalSubmit"
    />

    <InviteAcceptModal
      :open="inviteModalOpen"
      :invite="invitePreview"
      :loading="inviteLoading"
      :error="inviteError"
      @close="closeInviteModal"
      @accept="acceptInvite"
    />

    <InviteLinkModal
      :open="inviteLinkModalOpen"
      :link="inviteLinkValue"
      :group-name="inviteLinkGroupName"
      :status-message="inviteLinkStatusMessage"
      :status-tone="inviteLinkStatusTone"
      :can-share="inviteLinkCanShare"
      @close="closeInviteLinkModal"
      @copy="copyInviteLink"
      @share="shareInviteLink"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import AuthModal from '../components/AuthModal.vue';
import GroupManagerModal from '../components/GroupManagerModal.vue';
import InviteAcceptModal from '../components/InviteAcceptModal.vue';
import InviteLinkModal from '../components/InviteLinkModal.vue';
import NoteSidebar from '../components/NoteSidebar.vue';
import SyncStatusPill from '../components/SyncStatusPill.vue';
import TaskItem from '../components/TaskItem.vue';
import AdBanner from '../components/AdBanner.vue';
import CreateListModal from '../components/CreateListModal.vue';
import MoveListModal from '../components/MoveListModal.vue';
import ConfirmActionModal from '../components/ConfirmActionModal.vue';
import PlanModal from '../components/PlanModal.vue';
import api from '../services/api';
import { getMeta, setMeta } from '../services/idb';
import { NOTE_COLORS, normalizeNoteColor } from '../constants/note-colors';

const UI_STATE_META_KEY = 'home_ui_state_v1';

export default {
  name: 'HomeView',
  components: {
    AuthModal,
    GroupManagerModal,
    InviteAcceptModal,
    InviteLinkModal,
    NoteSidebar,
    SyncStatusPill,
    TaskItem,
    AdBanner,
    CreateListModal,
    MoveListModal,
    ConfirmActionModal,
    PlanModal,
  },
  data() {
    return {
      newTask: '',
      composerImageFile: null,
      composerImagePreview: '',
      composerImagePersistedDataUrl: '',
      composerStatePersistTimer: null,
      sidebarOpen: window.innerWidth > 980,
      isMobile: window.innerWidth <= 980,
      noteColors: NOTE_COLORS,
      createListModalOpen: false,
      createListPresetGroupId: '',
      createListMode: 'create',
      plansModalOpen: false,
      colorMenuOpen: false,
      editingTitle: false,
      editingTitleValue: '',
      isComposerDragOver: false,
      composerDragDepth: 0,
      moveRequest: null,
      moveListModalOpen: false,
      moveListModalState: 'idle',
      moveListModalCountdown: 3,
      moveListAutoCloseTimer: null,
      moveListCountdownTimer: null,
      draggingTaskId: null,
      taskDropTarget: null,
      taskOrderDraftByList: {},
      taskReorderPersistTimer: null,
      taskReorderPersistDelay: 180,
      taskReorderInFlight: false,
      pendingTaskReorder: null,
      groupLimitMessage: '',
      planModalHeading: 'Quitar anuncios y ampliar grupos',
      planModalMessage: '',
      sidebarSwipeActive: false,
      sidebarSwipeStartX: 0,
      sidebarSwipeDeltaX: 0,
      sidebarSwipeDragging: false,
      invitePreview: null,
      inviteModalOpen: false,
      inviteLoading: false,
      inviteError: '',
      inviteLinkModalOpen: false,
      inviteLinkValue: '',
      inviteLinkGroupName: '',
      inviteLinkStatusMessage: '',
      inviteLinkStatusTone: 'neutral',
      deferredInstallPrompt: null,
      installPromptAvailable: false,
      isStandaloneMode: false,
      floatingComposerOpen: false,
      composerVisible: true,
      composerObserver: null,
      keyboardInset: 0,
      visualViewportHeight: window.innerHeight,
      confirmActionModalOpen: false,
      confirmActionModalTitle: 'Confirmar acción',
      confirmActionModalDescription: '',
      confirmActionModalConfirmLabel: 'Confirmar',
      confirmActionModalTone: 'danger',
      confirmActionModalLoading: false,
      confirmActionModalPayload: null,
      notificationsMenuOpen: false,
      notificationsLoading: false,
      notificationsItems: [],
      notificationsUnread: 0,
    };
  },
  computed: {
    ...mapState([
      'mode',
      'user',
      'groups',
      'selectedListId',
      'online',
      'syncing',
      'socketConnected',
      'authModalOpen',
      'groupModalOpen',
      'billing',
      'saveStatus',
    ]),
    ...mapGetters(['visibleLists', 'currentList', 'currentTasks', 'pendingTaskIds']),
    orderedTasks() {
      const baseTasks = [...this.currentTasks].sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
      const listId = this.currentList?.id;
      if (!listId) return baseTasks;

      const draftOrder = this.taskOrderDraftByList[listId];
      if (!Array.isArray(draftOrder) || !draftOrder.length) {
        return baseTasks;
      }

      const tasksById = new Map(baseTasks.map((task) => [task.id, task]));
      const ordered = [];

      draftOrder.forEach((taskId) => {
        const task = tasksById.get(taskId);
        if (task) {
          ordered.push(task);
          tasksById.delete(taskId);
        }
      });

      baseTasks.forEach((task) => {
        if (tasksById.has(task.id)) {
          ordered.push(task);
        }
      });

      return ordered;
    },
    currentListTitle() {
      return this.currentList?.title || 'Mis notas';
    },
    currentListMeta() {
      if (!this.currentList) return 'Hoja personal';
      if (this.isQuickSheet) return 'Hoja rápida';
      return this.currentList.group_id ? 'Hoja grupal' : 'Hoja personal';
    },
    headerDescription() {
      return this.mode === 'guest'
        ? 'Tu hoja rápida abre al instante. Guarda y comparte iniciando sesión.'
        : 'Tus cambios se guardan automáticamente.';
    },
    resolvedListColor() {
      return normalizeNoteColor(this.currentList?.color);
    },
    showAds() {
      if (this.mode === 'guest') return true;
      if (!this.billing?.loaded) return false;
      const plan = String(this.billing?.plan || '').toLowerCase();
      const status = String(this.billing?.status || '').toLowerCase();
      const isExplicitlyFree = !plan && (!status || status === 'free');
      const hasNoAds = !!(
        this.billing?.ad_free_active
        || ['monthly', 'yearly', 'lifetime'].includes(plan)
        || ['active', 'trialing', 'processing', 'complete', 'paid'].includes(status)
      );
      return !hasNoAds && isExplicitlyFree;
    },
    notificationsEnabled() {
      return this.mode === 'user' && Number(this.user?.notifications_enabled ?? 1) === 1;
    },
    isQuickSheet() {
      return this.currentList?.id === 'guest-inbox' || Number(this.currentList?.is_quick_sheet || 0) === 1;
    },
    isCurrentListAuthor() {
      if (!this.currentList || this.isQuickSheet) return false;
      if (!this.currentList.group_id) return true;
      return Number(this.currentList.author_user_id || 0) === Number(this.user?.id || 0);
    },
    canDeleteCurrentList() {
      if (!this.currentList || this.isQuickSheet) return false;
      if (!this.currentList.group_id) return true;
      return this.isCurrentListAuthor;
    },
    showAuthorFooter() {
      return !!(this.currentList?.group_id && this.currentList?.author_name);
    },
    paperStyle() {
      const accent = this.resolvedListColor;
      return {
        '--note-accent': accent,
        '--note-accent-soft': `${accent}72`,
        '--note-accent-softer': `${accent}30`,
        '--note-accent-line': `${accent}C8`,
      };
    },
    listModalHeading() {
      return this.createListMode === 'saveQuick' ? 'Guardar hoja rápida' : 'Crear nota';
    },
    listModalEyebrow() {
      return this.createListMode === 'saveQuick' ? 'Guardar' : 'Nueva hoja';
    },
    listModalDescription() {
      if (this.createListMode === 'saveQuick') {
        return 'Se guardará como una hoja real y la hoja rápida quedará libre para nuevas capturas.';
      }
      return '';
    },
    listModalSubmitLabel() {
      return this.createListMode === 'saveQuick' ? 'Guardar hoja' : 'Crear hoja';
    },
    listModalPlaceholder() {
      return this.createListMode === 'saveQuick' ? 'Ej. Ideas para abril' : 'Ej. Compras de la semana';
    },
    moveListModalTitle() {
      if (!this.moveRequest?.list) return 'Mover hoja';
      if (this.moveRequest.targetScopeType === 'group') {
        return `Mover “${this.moveRequest.list.title}” a ${this.moveRequest.targetGroupName}`;
      }
      return `Mover “${this.moveRequest.list.title}” a notas personales`;
    },
    moveListModalDescription() {
      if (!this.moveRequest?.list) return '';
      if (this.moveRequest.targetScopeType === 'group') {
        return 'Esta hoja será visible para todos los miembros del grupo seleccionado. Solo el autor de la hoja podrá volver a moverla fuera del grupo o eliminarla.';
      }
      return 'Esta hoja dejará de ser visible para los miembros del grupo. Pasará a tus notas personales y conservará su autor original.';
    },
    sidebarSwipeWidth() {
      if (!this.isMobile) return 0;
      return Math.min(320, Math.max(280, window.innerWidth - 24));
    },
    sidebarSwipeProgress() {
      if (!this.isMobile) return this.sidebarOpen ? 1 : 0;
      if (this.sidebarOpen) return 1;
      if (!this.sidebarSwipeActive) return 0;
      return Math.max(0, Math.min(1, this.sidebarSwipeDeltaX / this.sidebarSwipeWidth));
    },
    sidebarShellStyle() {
      if (!this.isMobile) return {};
      if (this.sidebarOpen && !this.sidebarSwipeActive) return {};
      const hiddenX = -(this.sidebarSwipeWidth + 20);
      const translateX = hiddenX + (this.sidebarSwipeProgress * (this.sidebarSwipeWidth + 20));
      return {
        transform: `translateX(${translateX}px)`,
        opacity: this.sidebarSwipeProgress > 0 ? 1 : undefined,
        pointerEvents: this.sidebarOpen || this.sidebarSwipeActive ? 'auto' : undefined,
      };
    },
    drawerBackdropStyle() {
      if (!this.isMobile) return {};
      const opacity = (this.sidebarOpen ? 1 : this.sidebarSwipeProgress) * 0.92;
      return { opacity: String(opacity) };
    },
    inviteRouteToken() {
      return this.$route?.name === 'invite' ? String(this.$route.params?.token || '') : '';
    },
    canShowInstallButton() {
      const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent || '');
      return !this.isStandaloneMode && (this.installPromptAvailable || isIos);
    },
    inviteLinkCanShare() {
      return typeof navigator !== 'undefined' && typeof navigator.share === 'function' && !!this.inviteLinkValue;
    },
    showFloatingComposerFab() {
      return this.isMobile && !this.composerVisible && !this.floatingComposerOpen;
    },
    showFloatingComposerModal() {
      return this.isMobile && this.floatingComposerOpen;
    },
    floatingComposerOverlayStyle() {
      const baseBottom = this.keyboardInset > 0
        ? `calc(18px + env(safe-area-inset-bottom, 0px) + ${this.keyboardInset}px)`
        : 'calc(18px + env(safe-area-inset-bottom, 0px))';
      return {
        paddingBottom: baseBottom,
        alignItems: this.keyboardInset > 0 ? 'flex-end' : 'center',
      };
    },
    floatingComposerCardStyle() {
      const viewportHeight = Number(this.visualViewportHeight || window.innerHeight || 0);
      const maxHeight = Math.max(280, Math.floor(viewportHeight - 28));
      return {
        maxHeight: `${maxHeight}px`,
      };
    },
  },
  watch: {
    mode(value) {
      if (value === 'user' && this.inviteRouteToken) {
        this.inviteModalOpen = true;
        this.loadInvitePreview();
      }
      if (value === 'user' && this.notificationsEnabled) {
        this.loadNotificationsSummary();
      }
      if (value !== 'user') {
        this.notificationsMenuOpen = false;
        this.notificationsItems = [];
        this.notificationsUnread = 0;
      }
    },
    selectedListId() {
      this.restoreComposerStateForSelectedList();
    },
    newTask() {
      this.queuePersistComposerState();
    },
    '$route.fullPath'() {
      this.handleInviteRouteEntry();
    },
  },
  async mounted() {
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('click', this.handleDocumentClick);
    window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', this.handleAppInstalled);
    window.addEventListener('notas:notifications-changed', this.handleNotificationsChanged);
    window.addEventListener('notas:notifications-read', this.handleNotificationsRead);
    window.visualViewport?.addEventListener?.('resize', this.handleVisualViewportChange);
    window.visualViewport?.addEventListener?.('scroll', this.handleVisualViewportChange);
    this.refreshInstallState();
    this.syncVisualViewportState();
    this.registerComposerObserver();
    await this.initAppAction();
    await this.restoreComposerStateForSelectedList();
    if (this.notificationsEnabled) {
      await this.loadNotificationsSummary();
    }
    await this.handleInviteRouteEntry();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt);
    window.removeEventListener('appinstalled', this.handleAppInstalled);
    window.removeEventListener('notas:notifications-changed', this.handleNotificationsChanged);
    window.removeEventListener('notas:notifications-read', this.handleNotificationsRead);
    window.visualViewport?.removeEventListener?.('resize', this.handleVisualViewportChange);
    window.visualViewport?.removeEventListener?.('scroll', this.handleVisualViewportChange);
    this.composerObserver?.disconnect?.();
    this.clearComposerImage(false);
    this.clearMoveListTimers();
    this.clearTaskReorderTimers();
    this.flushComposerStatePersist();
  },
  methods: {
    ...mapActions({
      initAppAction: 'initApp',
      selectListAction: 'selectList',
      createListAction: 'createList',
      renameListAction: 'renameList',
      deleteListAction: 'deleteList',
      updateListColorAction: 'updateListColor',
      addTaskAction: 'addTask',
      attachImageToTaskAction: 'attachImageToTask',
      removeTaskImageAction: 'removeTaskImage',
      toggleTaskAction: 'toggleTask',
      renameTaskAction: 'renameTask',
      reorderTasksAction: 'reorderTasks',
      deleteTaskAction: 'deleteTask',
      loginAction: 'login',
      registerAction: 'register',
      logoutAction: 'logout',
      createGroupAction: 'createGroup',
      inviteToGroupAction: 'inviteToGroup',
      bootstrapAction: 'bootstrap',
      startCheckoutAction: 'startCheckout',
      openBillingPortalAction: 'openBillingPortal',
      saveQuickSheetAction: 'saveQuickSheetAsList',
      moveListToScopeAction: 'moveListToScope',
    }),
    handleResize() {
      this.isMobile = window.innerWidth <= 980;
      this.syncVisualViewportState();
      if (!this.isMobile) {
        this.sidebarOpen = true;
        this.resetSidebarSwipe();
        this.floatingComposerOpen = false;
        this.composerVisible = true;
        this.keyboardInset = 0;
      }
    },
    syncVisualViewportState() {
      const vv = window.visualViewport;
      if (!vv) {
        this.visualViewportHeight = window.innerHeight;
        this.keyboardInset = 0;
        return;
      }
      const delta = Math.max(0, Math.round(window.innerHeight - (vv.height + vv.offsetTop)));
      this.visualViewportHeight = Math.round(vv.height);
      this.keyboardInset = delta > 110 ? delta : 0;
    },
    handleVisualViewportChange() {
      this.syncVisualViewportState();
    },
    registerComposerObserver() {
      if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return;
      this.$nextTick(() => {
        this.composerObserver?.disconnect?.();
        const target = this.$refs.composerCard;
        if (!target) return;
        this.composerObserver = new IntersectionObserver((entries) => {
          const entry = entries?.[0];
          this.composerVisible = !!(entry?.isIntersecting && entry.intersectionRatio > 0.15);
          if (this.composerVisible && !this.newTask && !this.composerImagePreview) {
            this.floatingComposerOpen = false;
          }
        }, {
          threshold: [0, 0.15, 0.35, 1],
        });
        this.composerObserver.observe(target);
      });
    },
    focusComposerInput(target = 'top') {
      const refName = target === 'floating' ? 'taskComposerFloating' : 'taskComposerTop';
      this.$nextTick(() => {
        const input = this.$refs[refName];
        if (!input?.focus) return;
        const focusInput = () => {
          input.focus({ preventScroll: target === 'floating' });
          const length = String(input.value || '').length;
          input.setSelectionRange?.(length, length);
        };
        focusInput();
        if (target === 'floating') {
          requestAnimationFrame(() => {
            focusInput();
            window.setTimeout(focusInput, 120);
          });
        }
      });
    },
    openFloatingComposer() {
      this.floatingComposerOpen = true;
      this.syncVisualViewportState();
      this.focusComposerInput('floating');
    },
    closeFloatingComposer() {
      this.floatingComposerOpen = false;
    },
    resetSidebarSwipe() {
      this.sidebarSwipeActive = false;
      this.sidebarSwipeStartX = 0;
      this.sidebarSwipeDeltaX = 0;
      this.sidebarSwipeDragging = false;
    },
    closeSidebar() {
      this.sidebarOpen = false;
      this.resetSidebarSwipe();
    },
    shouldIgnoreSidebarSwipe(target) {
      return !!target?.closest?.('input,textarea,button,a,label,[data-task-id],.composer-card,.paper');
    },
    handleSidebarEdgeStart(event) {
      if (!this.isMobile || this.sidebarOpen) return;
      const touch = event.changedTouches?.[0];
      if (!touch) return;
      if (touch.clientX > 28) return;
      if (this.shouldIgnoreSidebarSwipe(event.target)) return;
      this.sidebarSwipeActive = true;
      this.sidebarSwipeDragging = false;
      this.sidebarSwipeStartX = touch.clientX;
      this.sidebarSwipeDeltaX = 0;
    },
    handleSidebarEdgeMove(event) {
      if (!this.sidebarSwipeActive || this.sidebarOpen) return;
      const touch = event.changedTouches?.[0];
      if (!touch) return;
      const deltaX = Math.max(0, touch.clientX - this.sidebarSwipeStartX);
      this.sidebarSwipeDeltaX = Math.min(this.sidebarSwipeWidth, deltaX);
      if (this.sidebarSwipeDeltaX > 8) {
        this.sidebarSwipeDragging = true;
      }
      if (this.sidebarSwipeDragging && event.cancelable) {
        event.preventDefault();
      }
    },
    handleSidebarEdgeEnd() {
      if (!this.sidebarSwipeActive) return;
      const shouldOpen = this.sidebarSwipeDeltaX >= (this.sidebarSwipeWidth * 0.32);
      this.sidebarOpen = shouldOpen;
      this.resetSidebarSwipe();
    },
    handleSidebarEdgeCancel() {
      this.resetSidebarSwipe();
    },
    queuePersistComposerState() {
      if (this.composerStatePersistTimer) {
        clearTimeout(this.composerStatePersistTimer);
      }
      this.composerStatePersistTimer = window.setTimeout(() => {
        this.composerStatePersistTimer = null;
        this.flushComposerStatePersist();
      }, 140);
    },
    async flushComposerStatePersist() {
      const listId = this.currentList?.id || this.selectedListId || 'guest-inbox';
      if (!listId) return;
      const currentState = await getMeta(UI_STATE_META_KEY, { selected_list_id: listId, composer_drafts: {} });
      const nextState = {
        ...(currentState || {}),
        selected_list_id: this.selectedListId || listId,
        composer_drafts: {
          ...(currentState?.composer_drafts || {}),
          [listId]: {
            text: String(this.newTask || ''),
            image_data_url: this.composerImagePersistedDataUrl || '',
            image_name: this.composerImageFile?.name || '',
            image_type: this.composerImageFile?.type || '',
          },
        },
      };
      await setMeta(UI_STATE_META_KEY, nextState);
    },
    async restoreComposerStateForSelectedList() {
      const listId = this.currentList?.id || this.selectedListId;
      if (!listId) return;
      const uiState = await getMeta(UI_STATE_META_KEY, null);
      const draft = uiState?.composer_drafts?.[listId] || null;
      const nextText = String(draft?.text || '');
      const nextImage = String(draft?.image_data_url || '');
      if (this.newTask !== nextText) {
        this.newTask = nextText;
      }
      if (!nextImage) {
        this.clearComposerImage(false);
        return;
      }
      if (this.composerImagePersistedDataUrl === nextImage && this.composerImagePreview) {
        return;
      }
      this.clearComposerImage(false);
      this.composerImagePersistedDataUrl = nextImage;
      this.composerImagePreview = nextImage;
      this.composerImageFile = this.dataUrlToFile(nextImage, draft?.image_name || 'imagen-pendiente.png', draft?.image_type || 'image/png');
    },
    dataUrlToFile(dataUrl, filename, mimeType = 'image/png') {
      try {
        const parts = String(dataUrl || '').split(',');
        if (parts.length < 2) return null;
        const mimeMatch = parts[0].match(/data:(.*?);base64/);
        const mime = mimeMatch?.[1] || mimeType;
        const binary = window.atob(parts[1]);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) {
          bytes[i] = binary.charCodeAt(i);
        }
        return new File([bytes], filename, { type: mime });
      } catch (error) {
        console.warn(error);
        return null;
      }
    },
    async fileToDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('No se pudo leer la imagen local.'));
        reader.readAsDataURL(file);
      });
    },
    toggleColorMenu() {
      this.colorMenuOpen = !this.colorMenuOpen;
    },
    async loadNotificationsSummary() {
      if (!this.notificationsEnabled) return;
      try {
        const response = await api.get('/notifications', { params: { limit: 20 } });
        const data = response.data?.data || {};
        this.notificationsItems = data.items || [];
        this.notificationsUnread = Number(data.unread_count || 0);
      } catch (error) {
        console.warn(error);
      }
    },
    async toggleNotificationsMenu() {
      this.notificationsMenuOpen = !this.notificationsMenuOpen;
      if (!this.notificationsMenuOpen) return;
      this.notificationsLoading = true;
      try {
        const response = await api.get('/notifications', { params: { limit: 30 } });
        const data = response.data?.data || {};
        this.notificationsItems = data.items || [];
        this.notificationsUnread = Number(data.unread_count || 0);
        if (this.notificationsUnread > 0) {
          await this.markNotificationsAsRead();
        }
      } catch (error) {
        console.warn(error);
      } finally {
        this.notificationsLoading = false;
      }
    },
    async markNotificationsAsRead() {
      try {
        const response = await api.post('/notifications/read-all', {});
        const unread = Number(response.data?.data?.unread_count || 0);
        this.notificationsUnread = unread;
        this.notificationsItems = this.notificationsItems.map((item) => ({ ...item, read: true }));
      } catch (error) {
        console.warn(error);
      }
    },
    async handleNotificationsChanged(event) {
      if (!this.notificationsEnabled) return;
      const payload = event?.detail || {};
      if (typeof payload.unread_count === 'number') {
        this.notificationsUnread = Number(payload.unread_count || 0);
      } else {
        await this.loadNotificationsSummary();
      }
      if (this.notificationsMenuOpen) {
        await this.loadNotificationsSummary();
      }
    },
    handleNotificationsRead(event) {
      const payload = event?.detail || {};
      this.notificationsUnread = Number(payload.unread_count || 0);
      this.notificationsItems = this.notificationsItems.map((item) => ({ ...item, read: true }));
    },
    async handleNotificationSelect(item) {
      const listId = String(item?.list_id || '');
      if (listId) {
        this.selectListAction(listId);
      }
      this.notificationsMenuOpen = false;
      if (this.isMobile) {
        this.closeSidebar();
      }
    },
    openAccountFromNotifications() {
      this.notificationsMenuOpen = false;
      this.goToAccount();
    },
    handleDocumentClick(event) {
      const target = event.target;
      if (!target.closest?.('.note-color-area')) {
        this.colorMenuOpen = false;
      }
      if (!target.closest?.('.notifications-shell')) {
        this.notificationsMenuOpen = false;
      }
      if (this.isMobile && this.sidebarOpen && !target.closest?.('.sidebar-shell') && !target.closest?.('.nav-toggle')) {
        this.closeSidebar();
      }
    },
    openAuth() {
      this.$store.commit('setAuthModalOpen', true);
    },
    closeAuth() {
      this.$store.commit('setAuthModalOpen', false);
    },
    refreshInstallState() {
      this.isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
      if (this.isStandaloneMode) {
        this.deferredInstallPrompt = null;
        this.installPromptAvailable = false;
      }
    },
    handleBeforeInstallPrompt(event) {
      event.preventDefault();
      this.deferredInstallPrompt = event;
      this.installPromptAvailable = true;
      this.refreshInstallState();
    },
    handleAppInstalled() {
      this.deferredInstallPrompt = null;
      this.installPromptAvailable = false;
      this.refreshInstallState();
    },
    async promptInstall() {
      this.refreshInstallState();
      if (this.isStandaloneMode) return;

      if (this.deferredInstallPrompt) {
        this.deferredInstallPrompt.prompt();
        try {
          await this.deferredInstallPrompt.userChoice;
        } catch (error) {
          console.warn(error);
        }
        this.deferredInstallPrompt = null;
        this.installPromptAvailable = false;
        return;
      }

      if (/iphone|ipad|ipod/i.test(window.navigator.userAgent || '')) {
        window.alert('En iPhone o iPad abre Compartir y luego toca “Agregar a pantalla de inicio”.');
        return;
      }

      window.alert('La instalación todavía no está disponible en este navegador. Intenta abrir la app en Chrome, Edge o Safari móvil.');
    },
    async handleInviteRouteEntry() {
      if (!this.inviteRouteToken) {
        this.inviteModalOpen = false;
        this.invitePreview = null;
        this.inviteError = '';
        return;
      }

      if (this.mode !== 'user') {
        this.inviteModalOpen = false;
        await this.loadInvitePreview();
        this.openAuth();
        return;
      }

      this.inviteModalOpen = true;
      await this.loadInvitePreview();
    },
    async loadInvitePreview() {
      if (!this.inviteRouteToken) return;
      this.inviteLoading = true;
      this.inviteError = '';
      try {
        const response = await api.get(`/invites/${this.inviteRouteToken}`);
        this.invitePreview = response.data?.data || null;
      } catch (error) {
        this.invitePreview = null;
        this.inviteError = error?.response?.data?.message || error?.message || 'No se pudo leer la invitación.';
      } finally {
        this.inviteLoading = false;
      }
    },
    async acceptInvite() {
      if (!this.inviteRouteToken) return;
      if (this.mode !== 'user') {
        this.openAuth();
        return;
      }

      this.inviteLoading = true;
      this.inviteError = '';
      try {
        await api.post(`/invites/${this.inviteRouteToken}/accept`);
        await this.bootstrapAction();
        this.inviteModalOpen = false;
        this.invitePreview = null;
        await this.$router.replace('/');
      } catch (error) {
        this.inviteError = error?.response?.data?.message || error?.message || 'No se pudo aceptar la invitación.';
        await this.loadInvitePreview();
      } finally {
        this.inviteLoading = false;
      }
    },
    async closeInviteModal() {
      this.inviteModalOpen = false;
      this.inviteError = '';
      if (this.$route.name === 'invite') {
        await this.$router.replace('/');
      }
    },
    openGroups() {
      if (this.mode === 'guest') return this.openAuth();
      this.groupLimitMessage = '';
      this.$store.commit('setGroupModalOpen', true);
    },
    closeGroups() {
      this.groupLimitMessage = '';
      this.$store.commit('setGroupModalOpen', false);
    },
    async doLogout() {
      await this.logoutAction();
    },
    goToAccount() {
      this.$router.push('/cuenta');
    },
    async handleSelectList(listId) {
      await this.flushComposerStatePersist();
      this.selectListAction(listId);
      if (this.isMobile) this.closeSidebar();
      this.editingTitle = false;
      this.draggingTaskId = null;
      this.taskDropTarget = null;
    },
    requestCreateList(payload = {}) {
      if (this.mode === 'guest' && (payload.groupId || payload.scopeType === 'group')) {
        this.openAuth();
        return;
      }
      this.createListMode = 'create';
      this.createListPresetGroupId = payload.groupId || '';
      this.createListModalOpen = true;
    },
    openQuickSaveModal() {
      if (this.mode !== 'user') {
        this.openAuth();
        return;
      }
      this.createListMode = 'saveQuick';
      this.createListPresetGroupId = '';
      this.createListModalOpen = true;
    },
    closeCreateListModal() {
      this.createListModalOpen = false;
      this.createListPresetGroupId = '';
      this.createListMode = 'create';
    },
    async submitCreateList({ title, groupId = null }) {
      try {
        if (this.createListMode === 'saveQuick') {
          await this.saveQuickSheetAction({ title, groupId });
        } else {
          await this.createListAction({
            title,
            groupId,
            color: this.noteColors[this.visibleLists.length % this.noteColors.length],
          });
        }
        this.closeCreateListModal();
        this.focusComposerInput('top');
      } catch (error) {
        window.alert(error?.response?.data?.message || error?.message || 'No se pudo completar la acción.');
      }
    },
    startTitleEdit() {
      if (!this.currentList || this.isQuickSheet) return;
      this.editingTitleValue = this.currentList.title;
      this.editingTitle = true;
      this.$nextTick(() => this.$refs.titleEditor?.focus?.());
    },
    async commitTitleEdit() {
      if (!this.editingTitle || !this.currentList) return;
      const value = this.editingTitleValue.trim();
      this.editingTitle = false;
      if (!value || value === this.currentList.title) return;
      await this.renameListAction({ listId: this.currentList.id, title: value });
    },
    async handleDeleteList(listId) {
      const list = this.visibleLists.find((item) => item.id === listId) || this.currentList;
      this.openConfirmActionModal({
        kind: 'delete-list',
        id: listId,
        title: 'Eliminar hoja',
        description: `Se eliminará ${list?.title ? `“${list.title}”` : 'esta hoja'} y sus tareas visibles para ti. Esta acción no se puede deshacer.`,
        confirmLabel: 'Eliminar hoja',
      });
    },
    requestMoveList(payload) {
      this.clearMoveListTimers();
      this.moveRequest = payload;
      this.moveListModalState = 'idle';
      this.moveListModalCountdown = 3;
      this.moveListModalOpen = true;
    },
    closeMoveListModal() {
      this.clearMoveListTimers();
      this.moveListModalOpen = false;
      this.moveListModalState = 'idle';
      this.moveListModalCountdown = 3;
      this.moveRequest = null;
    },
    clearMoveListTimers() {
      if (this.moveListAutoCloseTimer) {
        clearTimeout(this.moveListAutoCloseTimer);
        this.moveListAutoCloseTimer = null;
      }
      if (this.moveListCountdownTimer) {
        clearInterval(this.moveListCountdownTimer);
        this.moveListCountdownTimer = null;
      }
    },
    startMoveListAutoClose() {
      this.clearMoveListTimers();
      this.moveListModalCountdown = 3;
      this.moveListCountdownTimer = window.setInterval(() => {
        this.moveListModalCountdown = Math.max(0, this.moveListModalCountdown - 1);
      }, 1000);
      this.moveListAutoCloseTimer = window.setTimeout(() => {
        this.closeMoveListModal();
      }, 3000);
    },
    async confirmMoveList() {
      if (!this.moveRequest?.list || this.moveListModalState === 'loading') return;
      this.moveListModalState = 'loading';
      try {
        await this.moveListToScopeAction({
          listId: this.moveRequest.list.id,
          targetGroupId: this.moveRequest.targetScopeType === 'group' ? this.moveRequest.targetGroupId : null,
        });
        this.moveListModalState = 'success';
        this.startMoveListAutoClose();
      } catch (error) {
        this.moveListModalState = 'idle';
        window.alert(error?.response?.data?.message || error?.message || 'No se pudo mover la hoja.');
      }
    },
    closePlansModal() {
      this.plansModalOpen = false;
      this.planModalHeading = 'Quitar anuncios y ampliar grupos';
      this.planModalMessage = '';
    },
    openConfirmActionModal({ kind = '', id = '', title = 'Confirmar acción', description = '', confirmLabel = 'Confirmar', tone = 'danger' } = {}) {
      this.confirmActionModalPayload = { kind, id };
      this.confirmActionModalTitle = title;
      this.confirmActionModalDescription = description;
      this.confirmActionModalConfirmLabel = confirmLabel;
      this.confirmActionModalTone = tone;
      this.confirmActionModalLoading = false;
      this.confirmActionModalOpen = true;
    },
    closeConfirmActionModal(force = false) {
      if (this.confirmActionModalLoading && !force) return;
      this.confirmActionModalOpen = false;
      this.confirmActionModalPayload = null;
      this.confirmActionModalLoading = false;
    },
    async confirmActionModalSubmit() {
      if (!this.confirmActionModalPayload?.kind || !this.confirmActionModalPayload?.id || this.confirmActionModalLoading) return;
      const payload = { ...this.confirmActionModalPayload };
      this.confirmActionModalLoading = true;
      this.closeConfirmActionModal(true);
      try {
        if (payload.kind === 'delete-task') {
          await this.deleteTaskAction(payload.id);
        } else if (payload.kind === 'delete-list') {
          await this.deleteListAction(payload.id);
        }
      } catch (error) {
        window.alert(error?.response?.data?.message || error?.message || 'No se pudo completar la acción.');
      }
    },
    clearTaskReorderTimers() {
      if (this.taskReorderPersistTimer) {
        clearTimeout(this.taskReorderPersistTimer);
        this.taskReorderPersistTimer = null;
      }
    },
    setLocalTaskOrder(listId, orderedTaskIds) {
      this.taskOrderDraftByList = {
        ...this.taskOrderDraftByList,
        [listId]: [...orderedTaskIds],
      };
    },
    clearLocalTaskOrder(listId) {
      if (!this.taskOrderDraftByList[listId]) return;
      const next = { ...this.taskOrderDraftByList };
      delete next[listId];
      this.taskOrderDraftByList = next;
    },
    getStoreTaskOrder(listId) {
      return [...this.$store.state.tasks]
        .filter((item) => !item.deleted_at && item.list_id === listId)
        .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
        .map((item) => item.id);
    },
    queueTaskReorderPersist(listId, orderedTaskIds) {
      this.pendingTaskReorder = {
        listId,
        orderedTaskIds: [...orderedTaskIds],
      };

      this.clearTaskReorderTimers();
      this.taskReorderPersistTimer = window.setTimeout(() => {
        this.taskReorderPersistTimer = null;
        this.flushTaskReorderPersist();
      }, this.taskReorderPersistDelay);
    },
    async flushTaskReorderPersist() {
      if (this.taskReorderInFlight || !this.pendingTaskReorder) return;

      const payload = this.pendingTaskReorder;
      this.pendingTaskReorder = null;
      this.taskReorderInFlight = true;

      try {
        await this.reorderTasksAction(payload);
        const persistedOrder = this.getStoreTaskOrder(payload.listId);
        const localDraft = this.taskOrderDraftByList[payload.listId] || [];
        if (JSON.stringify(persistedOrder) === JSON.stringify(localDraft)) {
          this.clearLocalTaskOrder(payload.listId);
        }
      } catch (error) {
        window.alert(error?.response?.data?.message || error?.message || 'No se pudo reordenar la tarea.');
      } finally {
        this.taskReorderInFlight = false;
        if (this.pendingTaskReorder) {
          this.flushTaskReorderPersist();
        }
      }
    },
    handleTaskDragStart(taskId) {
      if (!taskId) return;
      this.draggingTaskId = taskId;
      this.taskDropTarget = null;
    },
    handleTaskDragEnter(payload) {
      if (!this.draggingTaskId || !payload?.taskId || this.draggingTaskId === payload.taskId) return;
      this.taskDropTarget = { taskId: payload.taskId };
    },
    handleTaskDragEnd() {
      this.draggingTaskId = null;
      this.taskDropTarget = null;
    },
    handleTaskTouchStart(taskId) {
      this.handleTaskDragStart(taskId);
    },
    handleTaskTouchMove(payload) {
      if (!this.draggingTaskId || !payload?.targetTaskId || payload.targetTaskId === this.draggingTaskId) return;
      this.taskDropTarget = { taskId: payload.targetTaskId };
    },
    handleTaskTouchDrop(payload) {
      if (!payload?.taskId) {
        this.handleTaskDragEnd();
        return;
      }
      if (!this.draggingTaskId) {
        this.draggingTaskId = payload.taskId;
      }
      this.handleTaskDrop({ taskId: payload?.targetTaskId || this.taskDropTarget?.taskId || payload.taskId });
    },
    handleTaskTouchEnd() {
      this.handleTaskDragEnd();
    },
    handleTaskDrop(payload) {
      const targetTaskId = payload?.taskId;

      if (!this.draggingTaskId || !this.currentList || !targetTaskId || this.draggingTaskId === targetTaskId) {
        this.handleTaskDragEnd();
        return;
      }

      const orderedIds = this.orderedTasks.map((task) => task.id);
      const fromIndex = orderedIds.indexOf(this.draggingTaskId);
      const targetIndex = orderedIds.indexOf(targetTaskId);
      if (fromIndex === -1 || targetIndex === -1) {
        this.handleTaskDragEnd();
        return;
      }

      const nextOrderedIds = [...orderedIds];
      nextOrderedIds.splice(fromIndex, 1);
      const reducedTargetIndex = nextOrderedIds.indexOf(targetTaskId);
      if (reducedTargetIndex === -1) {
        this.handleTaskDragEnd();
        return;
      }

      const movingDown = fromIndex < targetIndex;
      const insertIndex = movingDown ? reducedTargetIndex + 1 : reducedTargetIndex;
      nextOrderedIds.splice(insertIndex, 0, this.draggingTaskId);

      if (JSON.stringify(nextOrderedIds) !== JSON.stringify(orderedIds)) {
        this.setLocalTaskOrder(this.currentList.id, nextOrderedIds);
        this.queueTaskReorderPersist(this.currentList.id, nextOrderedIds);
      }

      this.handleTaskDragEnd();
    },
    async handleChangeListColor(color) {
      if (!this.currentList) return;
      await this.updateListColorAction({ listId: this.currentList.id, color: normalizeNoteColor(color) });
      this.colorMenuOpen = false;
    },
    handleComposerImage(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      this.setComposerFile(file);
      event.target.value = '';
    },
    async setComposerFile(file) {
      if (!file || !String(file.type || '').startsWith('image/')) return;
      this.clearComposerImage(false);
      this.composerImageFile = file;
      this.composerImagePersistedDataUrl = await this.fileToDataUrl(file);
      this.composerImagePreview = this.composerImagePersistedDataUrl || URL.createObjectURL(file);
      this.queuePersistComposerState();
    },
    clearComposerImage(shouldPersist = true) {
      if (this.composerImagePreview && this.composerImagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(this.composerImagePreview);
      }
      this.composerImagePreview = '';
      this.composerImageFile = null;
      this.composerImagePersistedDataUrl = '';
      if (shouldPersist) {
        this.queuePersistComposerState();
      }
    },
    handleComposerDragEnter(event) {
      if (![...(event.dataTransfer?.types || [])].includes('Files')) return;
      this.composerDragDepth += 1;
      this.isComposerDragOver = true;
    },
    handleComposerDragLeave() {
      this.composerDragDepth = Math.max(0, this.composerDragDepth - 1);
      if (this.composerDragDepth === 0) this.isComposerDragOver = false;
    },
    handleComposerDrop(event) {
      this.composerDragDepth = 0;
      this.isComposerDragOver = false;
      const file = event.dataTransfer?.files?.[0];
      if (file) this.setComposerFile(file);
    },
    async handleAddTask() {
      if (!this.newTask) return;
      const wasFloatingOpen = this.floatingComposerOpen;
      const createdTaskId = await this.addTaskAction(this.newTask);
      if (createdTaskId && this.composerImageFile) {
        try {
          await this.attachImageToTaskAction({ taskId: createdTaskId, file: this.composerImageFile });
        } catch (error) {
          window.alert(error?.message || 'No se pudo subir la imagen.');
        } finally {
          this.clearComposerImage();
        }
      }
      this.newTask = '';
      await this.flushComposerStatePersist();
      this.floatingComposerOpen = false;
      if (!wasFloatingOpen) {
        this.focusComposerInput('top');
      }
    },
    async handleToggleTask(taskId) { await this.toggleTaskAction(taskId); },
    async handleRenameTask(payload) { if (payload.body) await this.renameTaskAction(payload); },
    async handleDeleteTask(taskId) {
      const task = this.currentTasks.find((item) => item.id === taskId);
      const preview = String(task?.body || '').trim();
      const shortPreview = preview.length > 90 ? `${preview.slice(0, 90)}…` : preview;
      this.openConfirmActionModal({
        kind: 'delete-task',
        id: taskId,
        title: 'Eliminar tarea',
        description: shortPreview ? `Vas a eliminar “${shortPreview}”. Esta acción no se puede deshacer.` : 'Esta tarea se eliminará y no se podrá recuperar.',
        confirmLabel: 'Eliminar tarea',
      });
    },
    async handleAttachImage({ taskId, file }) {
      try {
        await this.attachImageToTaskAction({ taskId, file });
      } catch (error) {
        window.alert(error?.message || 'No se pudo subir la imagen.');
      }
    },
    async handleRemoveTaskImage(taskId) {
      const confirmed = window.confirm('¿Quitar la imagen de esta tarea?');
      if (!confirmed) return;
      try {
        await this.removeTaskImageAction(taskId);
      } catch (error) {
        window.alert(error?.message || 'No se pudo quitar la imagen.');
      }
    },
    async handleUpgrade(plan) {
      const planCode = typeof plan === 'string' ? plan : (plan?.code || plan?.key || plan?.id || 'yearly');
      try {
        this.closePlansModal();
        await this.startCheckoutAction(planCode);
      } catch (error) {
        window.alert(error?.response?.data?.message || error?.message || 'No se pudo iniciar Stripe.');
      }
    },
    async handleManageBilling() {
      try {
        await this.openBillingPortalAction();
      } catch (error) {
        window.alert(error?.response?.data?.message || error?.message || 'No se pudo abrir el portal de facturación.');
      }
    },
    async handleCreateGroup(payload) {
      try {
        this.groupLimitMessage = '';
        await this.createGroupAction(payload);
      } catch (error) {
        const status = Number(error?.response?.status || 0);
        const message = error?.response?.data?.messages?.error || error?.response?.data?.message || error?.message || 'No se pudo crear el grupo.';
        if (status === 422 && /solo 1 grupo|grupos ilimitados|plan actual permite solo 1 grupo|1 grupo activo/i.test(message)) {
          this.groupLimitMessage = 'Tu cuenta free ya alcanzó el límite de 1 grupo. Actualiza tu suscripción para crear más grupos y ampliar la colaboración.';
          this.closeGroups();
          this.planModalHeading = 'Actualiza tu plan para crear más grupos';
          this.planModalMessage = 'En modo free solo puedes tener 1 grupo. Al actualizar tu suscripción desbloqueas grupos ilimitados, hasta 100 usuarios por grupo y una experiencia sin anuncios.';
          this.plansModalOpen = true;
          return;
        }
        window.alert(message);
      }
    },
    async openInvitePrompt(group) {
      try {
        const response = await this.inviteToGroupAction({ groupId: group.id, data: {} });
        const link = response?.invite_url || (response?.accept_path ? `${window.location.origin.replace(/\/$/, '')}${response.accept_path}` : '');
        if (!link) {
          window.alert(response?.message || 'No se pudo generar el enlace de invitación.');
          return;
        }

        this.inviteLinkModalOpen = true;
        this.inviteLinkValue = link;
        this.inviteLinkGroupName = group?.name || '';
        this.inviteLinkStatusMessage = '';
        this.inviteLinkStatusTone = 'neutral';

        const didCopy = await this.tryCopyText(link);
        if (didCopy) {
          this.inviteLinkStatusMessage = 'Enlace copiado. Ya puedes pegarlo donde quieras.';
          this.inviteLinkStatusTone = 'success';
        } else {
          this.inviteLinkStatusMessage = 'Te mostramos el enlace en pantalla para que puedas copiarlo o compartirlo manualmente.';
          this.inviteLinkStatusTone = 'neutral';
        }
      } catch (error) {
        window.alert(error?.response?.data?.message || error?.message || 'No se pudo crear la invitación.');
      }
    },
    closeInviteLinkModal() {
      this.inviteLinkModalOpen = false;
      this.inviteLinkStatusMessage = '';
      this.inviteLinkStatusTone = 'neutral';
    },
    async copyInviteLink() {
      if (!this.inviteLinkValue) return;
      const didCopy = await this.tryCopyText(this.inviteLinkValue);
      if (didCopy) {
        this.inviteLinkStatusMessage = 'Enlace copiado. Ya puedes pegarlo donde quieras.';
        this.inviteLinkStatusTone = 'success';
        return;
      }
      this.inviteLinkStatusMessage = 'No se pudo copiar automáticamente en este dispositivo. Mantén presionado el enlace para copiarlo manualmente.';
      this.inviteLinkStatusTone = 'error';
    },
    async shareInviteLink() {
      if (!this.inviteLinkValue || typeof navigator === 'undefined' || typeof navigator.share !== 'function') return;
      try {
        await navigator.share({
          title: this.inviteLinkGroupName ? `Invitación a ${this.inviteLinkGroupName}` : 'Invitación a grupo',
          text: this.inviteLinkGroupName
            ? `Únete a mi grupo "${this.inviteLinkGroupName}" en Notas.`
            : 'Únete a mi grupo en Notas.',
          url: this.inviteLinkValue,
        });
        this.inviteLinkStatusMessage = 'Abrimos el menú para compartir.';
        this.inviteLinkStatusTone = 'success';
      } catch (error) {
        if (error?.name === 'AbortError') return;
        this.inviteLinkStatusMessage = 'No se pudo abrir el menú para compartir. Puedes copiar el enlace manualmente.';
        this.inviteLinkStatusTone = 'error';
      }
    },
    async tryCopyText(value) {
      const text = String(value || '');
      if (!text) return false;

      if (window.isSecureContext && navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(text);
          return true;
        } catch (error) {
          console.warn('Clipboard API no disponible en este contexto', error);
        }
      }

      const helper = document.createElement('textarea');
      helper.value = text;
      helper.setAttribute('readonly', 'readonly');
      helper.style.position = 'fixed';
      helper.style.top = '-9999px';
      helper.style.left = '-9999px';
      helper.style.opacity = '0';
      document.body.appendChild(helper);
      helper.focus();
      helper.select();
      helper.setSelectionRange?.(0, helper.value.length);

      let copied = false;
      try {
        copied = document.execCommand('copy');
      } catch (error) {
        copied = false;
      } finally {
        document.body.removeChild(helper);
      }

      return !!copied;
    },
    async handleLeaveGroup(group) {
      const confirmed = window.confirm(`¿Seguro que quieres salir de “${group.name}”?`);
      if (!confirmed) return;
      try {
        await api.post(`/groups/${group.id}/leave`, {});
        await this.bootstrapAction();
      } catch (error) {
        window.alert(error?.response?.data?.message || error?.message || 'No se pudo salir del grupo.');
      }
    },
    async handleDelegateGroup({ group, delegateToUserId }) {
      if (!delegateToUserId) {
        window.alert('Selecciona primero a la persona que se quedará con el grupo.');
        return;
      }
      const confirmed = window.confirm(`Vas a delegar “${group.name}” y salir del grupo. ¿Continuar?`);
      if (!confirmed) return;
      try {
        await api.post(`/groups/${group.id}/leave`, { delegate_to_user_id: delegateToUserId });
        await this.bootstrapAction();
      } catch (error) {
        window.alert(error?.response?.data?.message || error?.message || 'No se pudo delegar el grupo.');
      }
    },
    async handleDeleteGroup(group) {
      const confirmed = window.confirm(`Esto eliminará el grupo “${group.name}”, sus hojas y sus notas para todos. ¿Deseas continuar?`);
      if (!confirmed) return;
      try {
        await api.delete(`/groups/${group.id}`);
        await this.bootstrapAction();
      } catch (error) {
        window.alert(error?.response?.data?.message || error?.message || 'No se pudo eliminar el grupo.');
      }
    },
  },
};
</script>

<style scoped>
.page { min-height: 100vh; background: radial-gradient(circle at top left, rgba(255,255,255,.95), rgba(255,255,255,0) 28%), linear-gradient(180deg, #f6f7fb 0%, #eef1f7 100%); color: #1f2937; }
.drawer-backdrop { position: fixed; inset: 0; background: rgba(15,23,42,.42); z-index: 30; transition: opacity .22s ease; }
.edge-swipe-zone { position: fixed; top: 0; left: 0; bottom: 0; width: 24px; z-index: 35; touch-action: pan-y; }
.sidebar-shell { position: fixed; inset: 20px auto 20px 20px; width: min(312px, calc(100vw - 40px)); height: calc(100vh - 40px); max-height: calc(100vh - 40px); z-index: 40; opacity: 0; pointer-events: none; transform: translateX(-115%); transition: transform .24s ease, opacity .24s ease; }
.sidebar-shell.open { opacity: 1; pointer-events: auto; transform: translateX(0); }
.workspace { min-height: 100vh; padding: 24px 24px 28px; margin-left: 0; transition: margin-left .24s ease; }
.workspace.shifted { margin-left: 336px; }
.topbar, .topbar-left, .brand-line, .paper-head, .paper-title-row, .paper-head-actions, .top-actions, .paper-inline-actions { display: flex; align-items: center; gap: 16px; }
.topbar, .paper-head { justify-content: space-between; }
.nav-toggle { width: 50px; height: 50px; border: 1px solid rgba(148,163,184,.32); border-radius: 18px; background: rgba(255,255,255,.8); display: inline-grid; place-items: center; padding: 0; cursor: pointer; box-shadow: 0 14px 34px rgba(15,23,42,.08); -webkit-appearance: none; appearance: none; }
.nav-toggle-icon { width: 24px; height: 24px; display: block; }
.nav-toggle-icon path { fill: none; stroke: #0f172a; stroke-width: 2.25; stroke-linecap: round; stroke-linejoin: round; }
.brand-block { display:flex; gap:14px; align-items:center; }
.brand-line h1 { margin:0; font-size:28px; color:#111827; }
.brand-block p { margin:6px 0 0; color:#64748b; }
.top-btn, .composer-button, .ghost-btn { border:none; border-radius:16px; padding:12px 16px; font-weight:800; cursor:pointer; }
.top-btn.primary, .composer-button { background:#111827; color:white; }
.top-btn.muted, .ghost-btn { background: rgba(255,255,255,.86); color:#334155; border:1px solid rgba(148,163,184,.2); }
.content-area { margin-top: 22px; display: grid; gap: 18px; }
.composer-card, .paper-shell { position: relative; border-radius: 28px; border:1px solid rgba(148,163,184,.2); box-shadow: 0 18px 42px rgba(15,23,42,.07); }
.composer-card { padding: 20px; overflow: hidden; background: rgba(255,255,255,.88); }
.composer-card.dragging { border-color: rgba(59,130,246,.42); box-shadow: 0 20px 44px rgba(59,130,246,.16); }
.composer-drop-overlay { position:absolute; inset:0; display:grid; place-items:center; font-size:24px; font-weight:900; color:#1d4ed8; background: rgba(239,246,255,.92); z-index:2; }
.composer-copy { display:grid; gap:4px; }
.composer-label, .paper-kicker { font-size:12px; text-transform:uppercase; letter-spacing:.12em; color:#64748b; font-weight:800; }
.composer-copy p { margin:0; color:#475569; }
.composer-main { margin-top: 16px; display:grid; grid-template-columns: 1fr auto auto; gap:10px; }
.composer-input { width:100%; border-radius:16px; border:1px solid rgba(148,163,184,.2); background: rgba(248,250,252,.96); padding:14px 16px; font-size:16px; outline:none; }
.composer-attach { width:48px; height:48px; display:grid; place-items:center; border-radius:16px; border:1px solid rgba(148,163,184,.2); background: rgba(255,255,255,.9); font-size:22px; cursor:pointer; }
.composer-attach.disabled { opacity:.55; cursor:not-allowed; }
.composer-file { display:none; }
.composer-preview { margin-top:14px; display:flex; align-items:center; gap:14px; padding:12px; border-radius:18px; background: rgba(248,250,252,.92); }
.composer-preview-image { width:72px; height:72px; object-fit:cover; border-radius:16px; }
.composer-preview-copy { display:grid; gap:4px; }
.paper-shell { padding: 22px; background: radial-gradient(circle at top right, var(--note-accent-soft), transparent 54%), linear-gradient(135deg, var(--note-accent-softer), rgba(255,255,255,.34) 44%, rgba(255,255,255,.92) 100%), linear-gradient(180deg, rgba(255,255,255,.82), rgba(255,255,255,.94)); border-color: var(--note-accent-softer); }
.paper-title-text { margin:0; font-size: 30px; color:#111827; }
.paper-title-input { border:none; border-bottom: 2px solid rgba(59,130,246,.28); background: transparent; font-size: 30px; font-weight: 800; color:#111827; outline:none; padding:0; }
.title-edit-btn { width:36px; height:36px; border:none; border-radius:999px; background: rgba(59,130,246,.1); color:#1d4ed8; cursor:pointer; }
.save-status { margin-top:6px; font-size:13px; color:#64748b; }
.paper-inline-actions { margin-top: 12px; flex-wrap: wrap; gap: 10px; }
.inline-create-btn { background: rgba(255,255,255,.92); }
.note-color-area { position: relative; }
.color-icon-btn { width: 52px; height: 52px; min-width: 52px; border: 1px solid rgba(148,163,184,.2); border-radius: 16px; background: rgba(255,255,255,.9); display: inline-grid; place-items: center; cursor: pointer; box-shadow: 0 10px 24px rgba(15,23,42,.06); }
.color-preview { width:14px; height:14px; border-radius:50%; box-shadow:0 0 0 1px rgba(148,163,184,.3); }
.color-preview.large { width: 22px; height: 22px; box-shadow: 0 0 0 2px rgba(15,23,42,.08); }
.palette-popover { position:absolute; top: calc(100% + 10px); left:0; z-index:8; display:grid; gap:12px; width:min(320px, 90vw); padding:14px; border-radius:18px; background: rgba(255,255,255,.98); border:1px solid rgba(148,163,184,.2); box-shadow: 0 18px 34px rgba(15,23,42,.08); }
.palette-popover.right { right: 0; left: auto; }
.palette-label { font-size: 13px; font-weight: 800; color: #334155; }
.palette-swatches { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.swatch { width:34px; height:34px; border-radius:999px; border:2px solid white; box-shadow:0 0 0 1px rgba(148,163,184,.26); cursor:pointer; }
.swatch.active { box-shadow:0 0 0 2px rgba(15,23,42,.72); }
.custom-color { display:flex; align-items:center; justify-content:space-between; gap:10px; padding:8px 10px; border-radius:12px; background: rgba(248,250,252,.96); color:#334155; font-size:13px; font-weight:700; }
.custom-color input { width:40px; height:40px; border:none; background:none; padding:0; }
.notifications-shell { position: relative; }
.bell-btn { position: relative; width: 50px; height: 50px; min-width: 50px; border: 1px solid rgba(148,163,184,.24); border-radius: 18px; background: rgba(255,255,255,.88); display: inline-grid; place-items: center; cursor: pointer; box-shadow: 0 14px 34px rgba(15,23,42,.08); }
.bell-btn.active { background: rgba(255,255,255,.98); }
.bell-btn.hasUnread { border-color: rgba(245, 158, 11, .36); }
.bell-icon { width: 22px; height: 22px; fill: #0f172a; }
.bell-badge { position: absolute; top: -6px; right: -6px; min-width: 22px; height: 22px; padding: 0 6px; border-radius: 999px; background: #dc2626; color: #fff; display: inline-grid; place-items: center; font-size: 11px; font-weight: 800; box-shadow: 0 10px 18px rgba(220,38,38,.24); }
.notifications-popover { position: absolute; top: calc(100% + 10px); right: 0; z-index: 25; width: min(390px, calc(100vw - 28px)); max-height: min(70vh, 560px); overflow: auto; padding: 14px; border-radius: 20px; background: rgba(255,255,255,.98); border: 1px solid rgba(148,163,184,.2); box-shadow: 0 22px 48px rgba(15,23,42,.12); }
.notifications-head { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; margin-bottom: 12px; }
.notifications-head strong { display:block; color:#0f172a; }
.notifications-head small { display:block; margin-top:4px; color:#64748b; }
.soft-inline-btn { border:none; background: rgba(241,245,249,.96); color:#334155; border-radius: 12px; padding: 9px 12px; font-weight: 800; cursor: pointer; }
.notifications-empty { padding: 16px 6px; color: #64748b; font-size: 14px; }
.notifications-list { display:grid; gap:10px; }
.notification-item { width:100%; border:none; background: rgba(248,250,252,.96); border-radius: 16px; padding: 12px; display:grid; gap:10px; text-align:left; cursor:pointer; border: 1px solid rgba(148,163,184,.16); }
.notification-item.unread { background: rgba(255,251,235,.98); border-color: rgba(245,158,11,.22); }
.notification-main strong { color:#0f172a; font-size:14px; }
.notification-main p { margin:4px 0 0; color:#475569; font-size:13px; line-height:1.45; }
.notification-meta { display:flex; align-items:center; justify-content:space-between; gap:12px; color:#64748b; font-size:12px; }
.paper { position:relative; margin-top:16px; padding-left: 15px; border-radius: 24px; background: radial-gradient(circle at top right, var(--note-accent-soft), transparent 48%), linear-gradient(135deg, var(--note-accent-softer), rgba(255,255,255,.28) 40%, rgba(255,255,255,.94) 100%), linear-gradient(180deg, rgba(255,255,255,.76), rgba(255,255,255,.94)); border: 1px solid var(--note-accent-softer); overflow: visible; }
.margin-line { position:absolute; inset:0 auto 0 12px; width:2px; background: var(--note-accent-line); opacity:.38; }
.task-list { position: relative; list-style:none; padding:0 0 6px; margin:0; }
.empty-state { display:grid; place-items:center; gap:6px; padding:56px 16px; color:#64748b; text-align:center; }
.empty-icon { font-size:32px; }
.paper-footer { margin-top: 12px; color: #64748b; font-size: 13px; }
.bottom-ad { margin-bottom: 4px; }
.ghost-btn.danger { color:#b91c1c; }
.save-btn { background: rgba(16,185,129,.12); color: #047857; border-color: rgba(16,185,129,.18); }
.desktop-copy { display:inline; }
.mobile-copy { display:none; }

 .paper-head { position: relative; display:block; padding-right: 84px; }
.paper-head-copy { min-width: 0; padding-right: 0; }
.paper-head-actions { position: absolute; top: 0; right: 0; display: block; margin: 0; }
.paper-title-row { min-width: 0; }
.paper-title-text, .paper-title-input { min-width: 0; max-width: 100%; }
.paper-title-text { word-break: break-word; }
.top-actions { justify-content: space-between; flex-wrap: wrap; width: 100%; }
.new-sheet-btn { order: 0; margin-right: auto; }
.notifications-shell { order: 1; margin-left: auto; }
.note-color-area { position: relative; display:flex; justify-content:flex-end; align-items:flex-start; }
.palette-popover.right { right: 0; left: auto; transform-origin: top right; }
.sheet-delete-btn { margin-left: 0; }

.floating-composer-fab { position: fixed; right: 18px; bottom: calc(18px + env(safe-area-inset-bottom, 0px)); width: 58px; height: 58px; min-width: 58px; min-height: 58px; aspect-ratio: 1 / 1; padding: 0; border: none; border-radius: 50%; background: #111827; color: #fff; display: grid; place-items: center; font-size: 34px; line-height: 1; box-shadow: 0 18px 42px rgba(15,23,42,.24); z-index: 45; -webkit-appearance: none; appearance: none; }
.floating-composer-fab span { display: inline-block; transform: translateY(-1px); }
.floating-composer-overlay { position: fixed; inset: 0; z-index: 55; display: flex; padding: 16px; background: rgba(15,23,42,.34); backdrop-filter: blur(10px); align-items: center; }
.floating-composer-card { width: min(100%, 720px); margin: 0 auto; border-radius: 28px; box-shadow: 0 28px 80px rgba(15,23,42,.22); overflow: auto; overscroll-behavior: contain; }
.floating-composer-main { align-items: stretch; }
.fab-fade-enter-active, .fab-fade-leave-active, .composer-fade-enter-active, .composer-fade-leave-active { transition: opacity .22s ease, transform .24s ease; }
.fab-fade-enter-from, .fab-fade-leave-to { opacity: 0; transform: translateY(10px) scale(.92); }
.composer-fade-enter-from, .composer-fade-leave-to { opacity: 0; }
.composer-fade-enter-from .floating-composer-card, .composer-fade-leave-to .floating-composer-card { transform: translateY(18px) scale(.98); }
@supports (height: 100dvh) { .sidebar-shell { height: calc(100dvh - 40px); max-height: calc(100dvh - 40px); } }
@media (max-width: 980px) { .workspace.shifted { margin-left: 0; } .sidebar-shell { inset: 12px auto calc(12px + env(safe-area-inset-bottom, 0px)) 12px; width: min(312px, calc(100vw - 24px)); height: calc(100vh - 24px - env(safe-area-inset-bottom, 0px)); max-height: calc(100vh - 24px - env(safe-area-inset-bottom, 0px)); } }
@supports (height: 100dvh) { @media (max-width: 980px) { .sidebar-shell { height: calc(100dvh - 24px - env(safe-area-inset-bottom, 0px)); max-height: calc(100dvh - 24px - env(safe-area-inset-bottom, 0px)); } } }
@media (max-width: 780px) { .workspace { padding: 18px 16px 22px; } .topbar, .topbar-left { align-items:flex-start; flex-direction:column; } .paper-head { padding-right: 74px; } .top-actions, .paper-inline-actions { width:100%; flex-wrap:wrap; } .paper-head-actions { top: 0; right: 0; } .notifications-shell { margin-left: auto; } .composer-main { grid-template-columns: 1fr auto auto; } .paper-title-text, .paper-title-input { font-size: 26px; } .floating-composer-card { border-radius: 28px; } }
@media (max-width: 560px) { .brand-line { flex-wrap: wrap; } .composer-preview { flex-direction: column; align-items: flex-start; } .desktop-copy { display:none; } .mobile-copy { display:inline; } .floating-composer-main { grid-template-columns: 1fr auto auto; } .floating-composer-fab { right: 16px; bottom: calc(16px + env(safe-area-inset-bottom, 0px)); } }
</style>
